import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, MessageCircle, Languages } from 'lucide-react';
import { toast } from 'sonner';

export default function MessagesPage() {
    const { user } = useAuth();
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState('');
    const [other, setOther] = useState(null);
    const [ad, setAd] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const nav = useNavigate();
    const bottom = useRef(null);

    // pending deep-link (no thread yet, will create on first message)
    const [pending, setPending] = useState(null); // { to_user_id, ad_id, other, ad }

    const loadThreads = async () => {
        const { data } = await api.get('/me/threads');
        setThreads(data);
        return data;
    };

    // Initial + deep link handling
    useEffect(() => {
        (async () => {
            const data = await loadThreads();
            const toId = searchParams.get('to');
            const adId = searchParams.get('ad');
            if (toId) {
                // Try to find existing thread matching this participant + ad_id
                const match = data.find(t => t.other?.id === toId && (adId ? t.ad?.id === adId : true));
                if (match) {
                    setActiveId(match.id);
                    setPending(null);
                } else {
                    // No existing thread — start pending draft (no active thread yet)
                    try {
                        const [userRes, adRes] = await Promise.all([
                            api.get(`/users/by-id/${toId}`).catch(() => null),
                            adId ? api.get(`/ads/${adId}`).catch(() => null) : Promise.resolve(null),
                        ]);
                        const otherUser = userRes?.data || { id: toId, nickname: 'usuario', avatar_url: null };
                        const adObj = adRes?.data || null;
                        setPending({ to_user_id: toId, ad_id: adId, other: otherUser, ad: adObj });
                        setActiveId(null);
                        setMsgs([]);
                        setOther(otherUser);
                        setAd(adObj);
                    } catch {
                        setPending({ to_user_id: toId, ad_id: adId, other: { id: toId, nickname: 'usuario' }, ad: null });
                        setActiveId(null);
                    }
                }
                // Clear the query params so refresh doesn't re-trigger
                setSearchParams({}, { replace: true });
            } else if (data.length && !activeId) {
                setActiveId(data[0].id);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeId) return;
        setPending(null); // clear pending draft when switching to a real thread
        (async () => {
            const { data } = await api.get(`/messages/${activeId}`);
            setMsgs(data.messages);
            setOther(data.thread.other);
            setAd(threads.find(t => t.id === activeId)?.ad || null);
            setTimeout(() => bottom.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        })();
    }, [activeId, threads]);

    const send = async () => {
        const body = text.trim();
        if (!body) return;
        try {
            if (pending) {
                // First message → create a new thread
                const { data } = await api.post('/messages', {
                    to_user_id: pending.to_user_id,
                    ad_id: pending.ad_id || null,
                    text: body,
                });
                setText('');
                const list = await loadThreads();
                setActiveId(data.thread_id);
                setPending(null);
                toast.success('Mensaje enviado');
            } else {
                await api.post('/messages/reply', { thread_id: activeId, text: body });
                setText('');
                const { data } = await api.get(`/messages/${activeId}`);
                setMsgs(data.messages);
                loadThreads();
                setTimeout(() => bottom.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const showConversation = activeId || pending;

    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold tracking-tight">Mensajes</h1>
            <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)] min-h-[520px]">
                <aside className="col-span-12 md:col-span-4 rounded-2xl border bg-card overflow-y-auto" data-testid="messages-thread-list">
                    {pending && (
                        <button
                            onClick={() => { /* keep pending active */ }}
                            className="w-full flex items-center gap-3 p-3 border-b bg-red-50 text-left"
                            data-testid="pending-thread"
                        >
                            <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-semibold overflow-hidden">
                                {pending.other?.avatar_url ? <img src={pending.other.avatar_url} alt="" className="h-full w-full object-cover" /> : (pending.other?.nickname?.[0]?.toUpperCase() || '?')}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">@{pending.other?.nickname}</div>
                                <div className="text-xs text-red-600 truncate">Nueva conversación</div>
                                {pending.ad?.title && <div className="text-[10px] text-muted-foreground truncate mt-0.5">Sobre: {pending.ad.title}</div>}
                            </div>
                        </button>
                    )}
                    {threads.length === 0 && !pending ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">
                            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
                            No tienes conversaciones aún. Contacta un vendedor desde un anuncio.
                        </div>
                    ) : threads.map(t => (
                        <button key={t.id} onClick={() => setActiveId(t.id)}
                            data-testid={`thread-${t.id}`}
                            className={`w-full flex items-center gap-3 p-3 border-b hover:bg-secondary text-left ${activeId === t.id ? 'bg-secondary' : ''}`}>
                            <div className="h-10 w-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold overflow-hidden">
                                {t.other?.avatar_url ? <img src={t.other.avatar_url} alt="" className="h-full w-full object-cover" /> : (t.other?.nickname?.[0]?.toUpperCase() || '?')}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <div className="font-medium text-sm truncate">@{t.other?.nickname}</div>
                                    {t.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{t.unread}</span>}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{t.last_message}</div>
                                {t.ad?.title && <div className="text-[10px] text-muted-foreground truncate mt-0.5">Sobre: {t.ad.title}</div>}
                            </div>
                        </button>
                    ))}
                </aside>
                <section className="col-span-12 md:col-span-8 rounded-2xl border bg-card flex flex-col overflow-hidden">
                    {showConversation && other ? (
                        <>
                            <div className="border-b p-3 flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold overflow-hidden">
                                    {other.avatar_url ? <img src={other.avatar_url} alt="" className="h-full w-full object-cover" /> : other.nickname?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-medium text-sm">@{other.nickname}</div>
                                    {ad?.title && <div className="text-xs text-muted-foreground">Sobre: {ad.title}</div>}
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-list">
                                {pending && msgs.length === 0 ? (
                                    <div className="text-center text-sm text-muted-foreground py-8">
                                        <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                        Envía tu primer mensaje a @{pending.other?.nickname}
                                        {pending.ad?.title && <div className="mt-1 text-xs">Sobre: {pending.ad.title}</div>}
                                    </div>
                                ) : (
                                    msgs.map(m => {
                                        const mine = m.from_user_id === user.id;
                                        return (
                                            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-red-600 text-white rounded-br-md' : 'bg-secondary rounded-bl-md'}`}>
                                                    {m.translated || m.text}
                                                    {!mine && !m.translated && (
                                                        <button onClick={async () => {
                                                            try { const { data } = await api.post('/ai/translate', { text: m.text, target_lang: 'es' }); setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, translated: data.translated } : x)); toast.success('Traducido'); }
                                                            catch { toast.error('Error'); }
                                                        }} className="mt-1 text-[10px] opacity-70 hover:opacity-100 flex items-center gap-1" data-testid={`translate-${m.id}`}><Languages className="h-3 w-3" />Traducir</button>
                                                    )}
                                                    <div className={`mt-0.5 text-[10px] ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>{new Date(m.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={bottom} />
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t p-3 flex items-center gap-2">
                                <Input placeholder={pending ? `Escribe un mensaje a @${pending.other?.nickname}...` : "Escribe un mensaje..."} value={text} onChange={(e) => setText(e.target.value)} data-testid="messages-input" />
                                <Button type="submit" data-testid="messages-send"><Send className="h-4 w-4" /></Button>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Selecciona una conversación</div>
                    )}
                </section>
            </div>
        </div>
    );
}
